import React, { useState, useEffect } from "react";
import Select from "react-select"; // ✅ ADDED IMPORT for multi-select
import { api, apiEndpoints } from "../api/api";
import "./RetestForm.css";

const RetestForm = ({ assessmentId, onRetestSuccess }) => {
  // ✅ UPDATED: preparedBy is now an array for multi-select
  const [formData, setFormData] = useState({
    nextVersionId: "",
    assessmentStartDate: "",
    assessmentEndDate: "",
    reportReleaseDate: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    sdm: "",
    preparedBy: [], // Changed from "" to []
    reviewedBy: "",
    approvedBy: "",
  });

  // ✅ NEW: State variables to store fetched user lists
  const [testers, setTesters] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assistantManagers, setAssistantManagers] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ✅ NEW: useEffect to fetch all user lists for the dropdowns
  useEffect(() => {
    const fetchUsersByRole = async (url, setter) => {
      try {
        const response = await api.get(url);
        setter(response.data);
      } catch (error) {
        console.error(`Error fetching from ${url}:`, error);
        setError("Failed to load user lists for dropdowns.");
      }
    };

    fetchUsersByRole(apiEndpoints.getTesters, setTesters);
    fetchUsersByRole(apiEndpoints.getTeamLeads, setTeamLeads);
    fetchUsersByRole(apiEndpoints.getManagers, setManagers);
    fetchUsersByRole(apiEndpoints.getAssistantManagers, setAssistantManagers);
  }, []);

  // Fetch pre-filled data for the form
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = apiEndpoints.getRetestInfo(assessmentId);
        const response = await api.get(url);
        setFormData((prev) => ({
          ...prev,
          ...response.data,
          // Ensure preparedBy is an array, even if API sends something else
          preparedBy: Array.isArray(response.data.preparedBy) ? response.data.preparedBy : [],
        }));
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          "Failed to load re-test data. Please try again.";
        setError(errorMessage);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [assessmentId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ NEW: Dedicated handler for the 'react-select' multi-select component
  const handlePreparedByChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      preparedBy: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // ✅ UPDATED: Construct the resources array correctly for multi-select
    const preparedByResources = formData.preparedBy.map(name => ({
        taskId: 1, // taskId for 'Prepared By'
        resourceName: name
    }));

    const otherResources = [
        { taskId: 2, resourceName: formData.reviewedBy }, // taskId for 'Reviewed By'
        { taskId: 3, resourceName: formData.approvedBy }, // taskId for 'Approved By'
    ];

    const payload = {
      assessmentId: assessmentId,
      assessmentStartDate: formData.assessmentStartDate,
      assessmentEndDate: formData.assessmentEndDate,
      reportReleaseDate: formData.reportReleaseDate,
      date: formData.date,
      description: formData.description,
      sdm: formData.sdm,
      resources: [...preparedByResources, ...otherResources], // Combine all resources
    };

    try {
      await api.post(apiEndpoints.submitRetestVersion, payload);
      onRetestSuccess();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to submit re-test form. Please check the details and try again.";
      setError(errorMessage);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ NEW: Logic to prepare user lists for dropdowns (copied from your form)
  const allManagers = [...managers, ...assistantManagers];
  const seenUserIds = new Set();
  const uniqueManagers = [];
  for (const user of allManagers) {
    if (!seenUserIds.has(user.userId)) {
      seenUserIds.add(user.userId);
      uniqueManagers.push(user);
    }
  }
  const testerOptions = testers.map((user) => ({
    value: user.name,
    label: user.name,
  }));


  if (loading) {
    return (
      <div className="retest-form-container">
        <p>Loading Retest Form...</p>
      </div>
    );
  }

  return (
    <div className="retest-form-container card">
      <h2 className="retest-form-title">Create New Retest Version</h2>
      <p className="retest-form-subtitle">
        This assessment is awaiting re-test. Please fill out the details for the
        new version.
      </p>
      <form onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div className="form-grid-columns">
          {/* Version ID (Disabled) */}
          <div className="form-group">
            <label htmlFor="nextVersionId">New Version ID</label>
            <input
              type="text"
              id="nextVersionId"
              name="nextVersionId"
              className="form-input form-input-readonly"
              value={formData.nextVersionId}
              readOnly
              disabled
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <input
              type="text"
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>

          {/* Version Date */}
          <div className="form-group">
            <label htmlFor="date">Version Date</label>
            <input
              type="date"
              id="date"
              name="date"
              className="form-input"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          {/* Assessment Start Date */}
          <div className="form-group">
            <label htmlFor="assessmentStartDate">New Assessment Start Date</label>
            <input
              type="date"
              id="assessmentStartDate"
              name="assessmentStartDate"
              className="form-input"
              value={formData.assessmentStartDate}
              onChange={handleChange}
              required
            />
          </div>

          {/* Assessment End Date */}
          <div className="form-group">
            <label htmlFor="assessmentEndDate">New Assessment End Date</label>
            <input
              type="date"
              id="assessmentEndDate"
              name="assessmentEndDate"
              className="form-input"
              value={formData.assessmentEndDate}
              onChange={handleChange}
              required
            />
          </div>

          {/* Report Release Date */}
          <div className="form-group">
            <label htmlFor="reportReleaseDate">New Report Release Date</label>
            <input
              type="date"
              id="reportReleaseDate"
              name="reportReleaseDate"
              className="form-input"
              value={formData.reportReleaseDate}
              onChange={handleChange}
              required
            />
          </div>

          {/* ✅ UPDATED SDM Dropdown */}
          <div className="form-group">
            <label htmlFor="sdm">SDM</label>
            <select
              id="sdm"
              name="sdm"
              className="form-input"
              value={formData.sdm}
              onChange={handleChange}
              required
            >
              <option value="">-- Select SDM --</option>
              {uniqueManagers.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name} {user.roles[0]?.role && `(${user.roles[0].role})`}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ UPDATED Prepared By Dropdown (Multi-select) */}
          <div className="form-group">
            <label htmlFor="preparedBy">Prepared By</label>
            <Select
              id="preparedBy"
              isMulti
              options={testerOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select Testers"
              onChange={handlePreparedByChange}
              value={testerOptions.filter((option) =>
                formData.preparedBy.includes(option.value)
              )}
            />
          </div>

          {/* ✅ UPDATED Reviewed By Dropdown */}
          <div className="form-group">
            <label htmlFor="reviewedBy">Reviewed By</label>
            <select
              id="reviewedBy"
              name="reviewedBy"
              className="form-input"
              value={formData.reviewedBy}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Project Lead --</option>
              {teamLeads.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ UPDATED Approved By Dropdown */}
          <div className="form-group">
            <label htmlFor="approvedBy">Approved By</label>
            <select
              id="approvedBy"
              name="approvedBy"
              className="form-input"
              value={formData.approvedBy}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Manager --</option>
              {uniqueManagers.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name} {user.roles[0]?.role && `(${user.roles[0].role})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="retest-form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit New Version"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RetestForm;